import { FC, useState, useEffect, memo, useMemo } from 'react'
import clsx from 'clsx'

import RatingIcon from '../rating-icon/rating-icon'
import LikeShare from 'components/parts/like-share/like-share'
import useUnratedWebinar from 'store/common/useUnratedWebinar'

import { getAxiosSingle, postAxiosSingle } from 'pages/api/axios'
import { formatLecturs, formatDate } from '../utils'
import { RatingVoc, starPoints, InitRating } from '../constants'
import { URL_LIST } from 'constants/url'
import { ILectur, IObj } from 'constants/types/common.types'
import { originShare } from 'utils/analytics/other/share_material/share_material'
import { A_wa_raitingClick, originRaiting } from 'utils/analytics/webinars/webinat_active'

import styles from '../star-rating.module.scss'

interface IRatingTable {
  webinarId: number
  isPast?: boolean
  date?: Date
  lecturs?: ILectur[]
  origin: originRaiting
}

const RatingTable: FC<IRatingTable> = ({ webinarId, isPast, date, lecturs, origin }) => {
  const { unratedWebinar, setUnratedWebinar } = useUnratedWebinar()

  const [hoverRating, setHoverRating] = useState(InitRating)
  const [rating, setRating] = useState(InitRating)
  const [touches, setTouches] = useState({ start: 0, end: 0 })

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    setTouches({ ...touches, start: event.targetTouches[0].clientY })
  }

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    setTouches({ ...touches, end: event.targetTouches[0].clientY })
  }

  const handleTouchEnd = () => {
    if (touches.start - touches.end < -20) {
      closeHandler()
    }
  }

  const RELEVANCE = RatingVoc.RELEVANCE

  const getRating = (e: React.MouseEvent<HTMLDivElement>) => {
    const target: HTMLDivElement | null = (e.target as HTMLDivElement).closest('[data-rating]')
    if (target) return target.dataset.rating
  }

  const getRatingData = async () => {
    const data = (await getAxiosSingle(`${URL_LIST.RATE}?id=${webinarId}`)) as IObj<any>
    setRating({ ...rating, ...data })
  }

  const closeHandler = async () => {
    const data = {
      id: webinarId,
      close: true,
    }
    const responce = await postAxiosSingle(URL_LIST.RATE, {}, data)
    if (responce?.status === 200) {
      const newRating = responce.data.rating
      setRating({ ...rating, ...newRating })
    }
  }

  const saveRating = async (e: React.MouseEvent<HTMLDivElement>) => {
    const currRating = getRating(e)
    const data = {
      id: webinarId,
      rate: isNaN(Number(currRating)) ? currRating : Number(currRating),
    }
    const responce = await postAxiosSingle(URL_LIST.RATE, {}, data)
    if (responce?.status === 200) {
      const newRating = responce.data.rating
      setRating({ ...rating, ...newRating })
      A_wa_raitingClick(origin)
      if (isPast) {
        const timer = setTimeout(() => {
          closeHandler()
          clearTimeout(timer)
        }, 3000)
      }
    }
  }

  const showMouseOver = (e: React.MouseEvent<HTMLDivElement>) => {
    const currRating = getRating(e)
    currRating && setHoverRating({ ...hoverRating, [RELEVANCE]: Number(currRating) })
  }

  const showMouseLeave = () => setHoverRating({ ...hoverRating, [RELEVANCE]: 0 })

  const formattedLecturs: string = useMemo(() => (lecturs ? formatLecturs(lecturs) : ''), [lecturs])

  const formatedDate: any = useMemo(() => (date ? formatDate(date as Date) : ''), [date])

  useEffect(() => {
    if (!unratedWebinar || !rating.close) return
    const webinarData = { ...unratedWebinar }
    webinarData.rating = {
      ...unratedWebinar.rating,
      ...rating,
    }

    const timer = setTimeout(() => {
      setUnratedWebinar(webinarData)
      clearTimeout(timer)
    }, 1000)
  }, [rating.close])

  useEffect(() => {
    getRatingData()
  }, [])

  return (
    <>
      <div
        className={clsx(styles.ratingTableWrapper, {
          [styles.ratingTableWrapperPast]: isPast,
          [styles.ratingTableWrapperPastClose]: isPast && rating.close,
        })}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
      >
        <div className={clsx(styles.ratingTableItem, { [styles.ratingTableItemPast]: isPast })}>
          {isPast && <div className={styles.close} onClick={closeHandler}></div>}
          {isPast && rating[RELEVANCE] > InitRating[RatingVoc.RELEVANCE] ? (
            <p className={styles.question}>Спасибо за оценку! 😊</p>
          ) : (
            <>
              {isPast ? (
                <div>
                  <p className={styles.questionPast}>Оцените прошедший вебинар</p>
                  {lecturs && <p className={styles.lecturs}>{formattedLecturs}</p>}
                  {date && <p className={styles.timePast}>{formatedDate}</p>}
                </div>
              ) : (
                <p className={styles.question}>Оцените вебинар</p>
              )}
              <div
                className={styles.ratingBox}
                onClick={saveRating}
                onMouseOver={showMouseOver}
                onMouseLeave={showMouseLeave}
              >
                {starPoints.map((index) => {
                  const isActive = hoverRating[RELEVANCE]
                    ? hoverRating[RELEVANCE] >= index
                    : rating[RELEVANCE] >= index
                  return (
                    <RatingIcon index={index} key={`${RELEVANCE}_${index}`} isActive={isActive} />
                  )
                })}
              </div>
            </>
          )}
        </div>
        {!isPast && (
          <LikeShare
            AData={{ origin: originShare.WEBINAR_FUTURE }}
            modificator={styles.likeShare}
            menuModificator={styles.shareMenu}
            noLike
          />
        )}
      </div>
    </>
  )
}

export default memo(RatingTable)
